import axios from "axios";
import { hydrateStatsPayload } from "./dashboardStatsHydration";

// API base URL - the Django REST API for MBS Hukuk
// Dev: use "/api" + setupProxy.js → local Django (avoids CORS).
// Prod: REACT_APP_API_BASE_URL or sistem.mbshukuk.com/api
const configuredBase = process.env.REACT_APP_API_BASE_URL;
export const API_BASE_URL =
    configuredBase !== undefined && configuredBase !== ""
        ? configuredBase
        : process.env.NODE_ENV === "development"
          ? "/api"
          : "https://sistem.mbshukuk.com/api";

// Origin used to absolutise relative file URLs returned by the API
// (e.g. "/media/uploads/foo.zip" → "https://sistem.mbshukuk.com/media/...").
export const API_ORIGIN = (() => {
    try {
        return new URL(API_BASE_URL).origin;
    } catch {
        return "https://sistem.mbshukuk.com";
    }
})();

/** Make a possibly-relative URL absolute against API_ORIGIN. */
export function absoluteUrl(url) {
    if (!url) return null;
    let out = url;
    if (!/^https?:\/\//i.test(url)) {
        out = url.startsWith("/") ? `${API_ORIGIN}${url}` : `${API_ORIGIN}/${url}`;
    }
    if (API_ORIGIN.startsWith("https:") && /^http:\/\//i.test(out)) {
        out = `https://${out.slice(7)}`;
    }
    return out;
}



/** SPA gezinince PDF lightbox kalmasın (WhatsApp vb. sayfalarda taşma). */
export function mbsDismissBlobLightbox() {
    const el = document.getElementById("mbs-blob-lightbox");
    if (el) el.remove();
}
(function bindBlobLightboxNavCleanup() {
    if (typeof window === "undefined" || window.__mbsBlobLightboxNavBound) return;
    window.__mbsBlobLightboxNavBound = true;
    const dismiss = () => mbsDismissBlobLightbox();
    window.addEventListener("popstate", dismiss);
    try {
        const wrap = (fnName) => {
            const orig = history[fnName];
            if (typeof orig !== "function") return;
            history[fnName] = function patchedHistory() {
                dismiss();
                return orig.apply(this, arguments);
            };
        };
        wrap("pushState");
        wrap("replaceState");
    } catch {
        /* ignore */
    }
})();

/** Mobile / iPad detection (incl. iPadOS desktop UA). */
export function isMobileBrowser() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    if (/iPhone|iPad|iPod|Android/i.test(ua)) return true;
    return navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
}

/**
 * Mobilde PDF/dosya indirme: async sonrası window.open Safari'de engellenir.
 * Mobil PDF → tam ekran önizleme + Paylaş/Kaydet; masaüstü → indir + sekmede aç.
 */
export async function openOrDownloadBlob(blob, filename = "dosya.pdf", opts = {}) {
    if (!blob) throw new Error("Dosya boş");
    const mime =
        (blob.type && !String(blob.type).includes("json") && !String(blob.type).includes("octet-stream")
            ? blob.type
            : null) ||
        opts.mime ||
        (/\.pdf$/i.test(filename) ? "application/pdf" : "application/octet-stream");
    const fileBlob =
        blob.type === mime ? blob : new Blob([blob], { type: mime });
    const mobile = isMobileBrowser();
    const isPdf = /pdf/i.test(mime) || /\.pdf$/i.test(filename);
    const url = URL.createObjectURL(fileBlob);

    if (mobile && isPdf) {
        showBlobLightbox(url, filename, mime, fileBlob);
        return { method: "lightbox" };
    }

    // Non-PDF mobile: share if possible, else download click
    if (mobile && !isPdf) {
        const shared = await tryShareFile(fileBlob, filename);
        if (shared === "shared") {
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            return { method: "share" };
        }
        if (shared === "abort") {
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            return { method: "share-abort" };
        }
    }

    try {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.rel = "noopener";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    } catch {
        /* ignore */
    }

    if (!mobile && isPdf) {
        const win = window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 120000);
        return { method: win ? "open" : "download" };
    }

    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return { method: "download" };
}

async function tryShareFile(fileBlob, filename) {
    if (
        typeof navigator === "undefined" ||
        typeof navigator.share !== "function" ||
        typeof navigator.canShare !== "function"
    ) {
        return null;
    }
    try {
        const file = new File([fileBlob], filename, {
            type: fileBlob.type || "application/octet-stream",
        });
        if (!navigator.canShare({ files: [file] })) return null;
        await navigator.share({ files: [file], title: filename });
        return "shared";
    } catch (err) {
        if (err && err.name === "AbortError") return "abort";
        return null;
    }
}

function showBlobLightbox(url, filename, mime, fileBlob) {
    mbsDismissBlobLightbox();
    const existing = null;

    const wrap = document.createElement("div");
    wrap.id = "mbs-blob-lightbox";
    wrap.setAttribute("role", "dialog");
    wrap.setAttribute("aria-modal", "true");
    wrap.style.cssText =
        "position:fixed;inset:0;z-index:20000;background:rgba(0,0,0,.92);" +
        "display:flex;flex-direction:column;" +
        "padding:max(12px, env(safe-area-inset-top)) 12px max(12px, env(safe-area-inset-bottom));";

    const bar = document.createElement("div");
    bar.style.cssText =
        "display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:10px;color:#fff;";

    const title = document.createElement("div");
    title.textContent = filename || "PDF";
    title.style.cssText =
        "font-size:14px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0;";

    const actions = document.createElement("div");
    actions.style.cssText = "display:flex;gap:8px;flex-shrink:0;";

    const mkBtn = (label, primary) => {
        const b = document.createElement("button");
        b.type = "button";
        b.textContent = label;
        b.style.cssText =
            "border:0;border-radius:8px;padding:10px 12px;font-size:13px;font-weight:600;cursor:pointer;" +
            (primary
                ? "background:#00a884;color:#041b16;"
                : "background:rgba(255,255,255,.12);color:#fff;");
        return b;
    };

    const close = () => {
        wrap.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1500);
    };

    const shareBtn = mkBtn("Kaydet / Paylaş", true);
    shareBtn.onclick = async () => {
        const shared = await tryShareFile(fileBlob, filename);
        if (shared === "shared") {
            // iOS Files / paylaşım tamam
            return;
        }
        if (shared === "abort") return;
        // Share yoksa indirme dene
        try {
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.rel = "noopener";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch {
            window.open(url, "_blank");
        }
    };

    const closeBtn = mkBtn("Kapat", false);
    closeBtn.onclick = close;

    actions.appendChild(shareBtn);
    actions.appendChild(closeBtn);
    bar.appendChild(title);
    bar.appendChild(actions);

    const frame = document.createElement("iframe");
    frame.title = filename || "Önizleme";
    frame.src = url;
    frame.style.cssText =
        "flex:1;width:100%;border:0;border-radius:10px;background:#fff;min-height:0;";

    wrap.appendChild(bar);
    wrap.appendChild(frame);
    document.body.appendChild(wrap);
}

/** Kimlik doğrulamalı GET → tarayıcıda dosya indir (PDF, CSV, …). */
async function parseBlobApiError(err, fallback = "İndirme başarısız") {
    const data = err?.response?.data;
    if (data instanceof Blob) {
        try {
            const text = await data.text();
            try {
                const json = JSON.parse(text);
                return json.detail || json.error || fallback;
            } catch {
                return text.slice(0, 240) || fallback;
            }
        } catch {
            return fallback;
        }
    }
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail) return detail;
    if (Array.isArray(detail)) return detail.join(" ");
    return err?.message || fallback;
}

export async function downloadAdminBlobGet(path, filename, params = {}, timeout = 120000) {
    let r;
    try {
        r = await api.get(path, { params, responseType: "blob", timeout });
    } catch (err) {
        throw new Error(await parseBlobApiError(err, "Dosya indirilemedi"));
    }
    const blob = r.data;
    if (blob?.type && blob.type.includes("json")) {
        const text = await blob.text();
        try {
            const json = JSON.parse(text);
            throw new Error(json.detail || json.error || "İndirme başarısız");
        } catch (err) {
            if (err instanceof Error && err.message !== "İndirme başarısız") throw err;
            throw new Error(text.slice(0, 200) || "İndirme başarısız");
        }
    }
    const ctype = r.headers?.["content-type"] || "application/octet-stream";
    await openOrDownloadBlob(new Blob([blob], { type: ctype }), filename, {
        mime: ctype,
    });
}

/** Trigger a browser file download / open via a hidden anchor click. */
export function triggerDownload(url) {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

/**
 * Kimlik doğrulamalı GET ile dosya indir (ZIP/DOCX — /media SSL sorunlarını aşar).
 * pathParts: örn. [id, "indir_zip"]
 */
export async function downloadAdminFile(resource, pathParts, filename = "indir") {
    const path = `/v1/admin/${resource}/${pathParts
        .filter((p) => p !== null && p !== undefined && p !== "")
        .join("/")}/`;
    const r = await api.get(path, { responseType: "blob" });
    const ctype =
        r.headers?.["content-type"] || "application/octet-stream";
    const blob = new Blob([r.data], { type: ctype });
    const safeName =
        filename ||
        (ctype.includes("pdf") ? "rapor.pdf" : "indir");
    await openOrDownloadBlob(blob, safeName, { mime: ctype });
}

export const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
});

const TOKEN_KEY = "mbs_auth_token";
const USER_KEY = "mbs_auth_user";

export function getToken() {
    return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token) {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
}
export function getUser() {
    try {
        const raw = localStorage.getItem(USER_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}
export function setUser(user) {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
}

api.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Token ${token}`;
    }
    if (config.data instanceof FormData && config.headers) {
        delete config.headers["Content-Type"];
    }
    return config;
});

api.interceptors.response.use(
    (res) => res,
    (err) => {
        if (err?.response?.status === 401) {
            setToken(null);
            setUser(null);
            if (
                typeof window !== "undefined" &&
                !window.location.pathname.startsWith("/login")
            ) {
                window.location.href = "/login";
            }
        }
        return Promise.reject(err);
    },
);

// Generic CRUD helpers for Django REST endpoints under /v1/admin/<resource>/
export const crud = {
    list: (resource, params = {}) =>
        api.get(`/v1/admin/${resource}/`, { params }).then((r) => r.data),
    retrieve: (resource, id) =>
        api.get(`/v1/admin/${resource}/${id}/`).then((r) => r.data),
    create: (resource, payload) =>
        api.post(`/v1/admin/${resource}/`, payload).then((r) => r.data),
    update: (resource, id, payload, opts = {}) => {
        const force = Boolean(opts?.force);
        const body = { ...(payload || {}) };
        if (force) body.force = true;
        const config = force ? { params: { force: "1" } } : {};
        return api
            .patch(`/v1/admin/${resource}/${id}/`, body, config)
            .then((r) => r.data);
    },
    remove: (resource, id) =>
        api.delete(`/v1/admin/${resource}/${id}/`).then((r) => r.data),
    /** Custom DRF @action endpoint: POST /v1/admin/<resource>/<id>/<action>/ */
    action: (resource, id, action, payload = {}) =>
        api
            .post(`/v1/admin/${resource}/${id}/${action}/`, payload)
            .then((r) => r.data),
    /** Download a binary file (PDF, ZIP, …) via authenticated request and
     *  open it in a new browser tab. Path is appended after /v1/admin/.
     *  Example: openFile("aracilar", id, "pdf_rapor")            */
    downloadFile: (resource, id, action, filename = "indir.zip") =>
        downloadAdminFile(resource, [id, action], filename),
    openFile: async (resource, ...pathParts) => {
        const path = `/v1/admin/${resource}/${pathParts
            .filter((p) => p !== null && p !== undefined && p !== "")
            .join("/")}/`;
        const r = await api.get(path, { responseType: "blob", timeout: 120000 });
        const blobData = r.data;
        if (blobData?.type && blobData.type.includes("json")) {
            const errText = await blobData.text();
            try {
                const json = JSON.parse(errText);
                throw new Error(json.detail || json.error || "PDF oluşturulamadı");
            } catch (err) {
                if (err instanceof Error && !err.message.includes("{")) throw err;
                throw new Error(errText.slice(0, 200) || "PDF oluşturulamadı");
            }
        }
        const ctype = r.headers?.["content-type"] || "application/pdf";
        const blob = new Blob([blobData], { type: ctype });
        const idPart = pathParts.find((p) => p !== null && p !== undefined && p !== "" && String(p) !== "pdf_rapor") || "rapor";
        const filename = `${resource}-${idPart}.pdf`;
        return openOrDownloadBlob(blob, filename, { mime: ctype });
    },
    /**
     * Multipart upload for endpoints that accept files (e.g. /v1/admin/evraklar/).
     * `payload` is a plain object whose values may be File instances or scalars.
     */
    upload: (resource, payload, onUploadProgress) => {
        const fd = new FormData();
        for (const [k, v] of Object.entries(payload || {})) {
            if (v === null || v === undefined || v === "") continue;
            if (v instanceof File || v instanceof Blob) {
                fd.append(k, v);
            } else {
                fd.append(k, typeof v === "object" ? JSON.stringify(v) : String(v));
            }
        }
        // Debug: log every FormData entry so we can verify the multipart body
        // shape in the browser console (visible only when devtools is open).
        try {
            // eslint-disable-next-line no-console
            console.groupCollapsed(
                `[upload] POST /v1/admin/${resource}/`,
            );
            for (const [k, val] of fd.entries()) {
                if (val instanceof File) {
                    // eslint-disable-next-line no-console
                    console.log(
                        `  ${k}: File(name="${val.name}", size=${val.size}B, type="${val.type}")`,
                    );
                } else {
                    // eslint-disable-next-line no-console
                    console.log(`  ${k}:`, val);
                }
            }
            // eslint-disable-next-line no-console
            console.groupEnd();
        } catch {
            /* ignore */
        }
        // Important: do NOT set Content-Type manually — axios needs to add the
        // multipart boundary itself.
        return api
            .post(`/v1/admin/${resource}/`, fd, {
                onUploadProgress,
            })
            .then((r) => r.data);
    },
    /** PATCH with multipart (dosya güncelleme vb.) */
    uploadPatch: (resource, id, payload, onUploadProgress) => {
        const fd = new FormData();
        for (const [k, v] of Object.entries(payload || {})) {
            if (v === null || v === undefined || v === "") continue;
            if (v instanceof File || v instanceof Blob) {
                fd.append(k, v);
            } else {
                fd.append(k, typeof v === "object" ? JSON.stringify(v) : String(v));
            }
        }
        return api
            .patch(`/v1/admin/${resource}/${id}/`, fd, {
                onUploadProgress,
            })
            .then((r) => r.data);
    },
};

crud.exportCsv = async (resource, params = {}) => {
    const r = await api.get(`/v1/admin/${resource}/export-csv/`, {
        params,
        responseType: "blob",
    });
    let csvText = await r.data.text();
    if (!csvText.startsWith("\ufeff")) {
        csvText = "\ufeff" + csvText;
    }
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${resource}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
};

export async function bulkUpdateList(resource, payload) {
    const r = await api.post(`/v1/admin/${resource}/bulk-update/`, payload);
    return r.data;
}

function quoteDosyaNot(metin) {
    return String(metin || "")
        .trim()
        .split("\n")
        .slice(0, 8)
        .map((line) => `> ${line}`)
        .join("\n");
}

/** Operasyon dosya notları — okundu / çözüldü / alıntılı cevap */
export const dosyaNotApi = {
    okundu: (id) => api.post(`/v1/admin/dosya-yorumlari/${id}/okundu/`).then((r) => r.data),
    cozuldu: (id, cozuldu = true) =>
        api.post(`/v1/admin/dosya-yorumlari/${id}/cozuldu/`, { cozuldu }).then((r) => r.data),
    cevapla: async (id, metin, alinti = true) => {
        const body = (metin || "").trim();
        try {
            const r = await api.post(`/v1/admin/dosya-yorumlari/${id}/cevapla/`, {
                metin: body,
                alinti,
            });
            return r.data;
        } catch (err) {
            const status = err?.response?.status;
            if (status !== 404 && status !== 405) throw err;
            const ust = (await api.get(`/v1/admin/dosya-yorumlari/${id}/`)).data;
            let metinOut = body;
            if (alinti && ust?.metin) {
                const quoted = quoteDosyaNot(ust.metin);
                metinOut = quoted ? `${quoted}\n\n${body}` : body;
            }
            const r = await api.post(`/v1/admin/dosya-yorumlari/`, {
                sigorta_dosyasi: ust.sigorta_dosyasi,
                ust_yorum: id,
                metin: metinOut,
                hatirlat: true,
            });
            return r.data;
        }
    },
};

export const siteStatsDisplay = {
    get: () => api.get("/v1/admin/site-stats-display/").then((r) => r.data),
    save: (payload) =>
        api.put("/v1/admin/site-stats-display/", payload).then((r) => r.data),
};

crud.exportDetailPdf = (resource, id, action = "pdf_rapor") =>
    crud.openFile(resource, id, action);

export const stats = (params = {}) =>
    api
        .get(`/v1/admin/stats/`, { params })
        .then((r) => hydrateStatsPayload(r.data));

export const schema = (appLabel, modelName) =>
    api
        .get(`/v1/admin/schema/${appLabel}/${modelName}/`)
        .then((r) => r.data);

export const login = (payload) =>
    api
        .post(`/login/`, payload)
        .then((r) => r.data);

/** İş akışı kuralları */
export const workflowRules = () =>
    api.get(`/v1/admin/workflow/rules/`).then((r) => r.data);

export const workflowValidate = (payload) =>
    api.post(`/v1/admin/workflow/validate/`, payload).then((r) => r.data);

export const workflowTransition = (payload) =>
    api.post(`/v1/admin/workflow/transition/`, payload).then((r) => r.data);

/** Slider — başlığa göre otomatik hero arka plan PNG (her seed farklı kompozisyon) */
export const sliderGenerateBackground = (baslik, altBaslik = "", seed = null) =>
    api
        .post(
            `/v1/admin/slider/generate-background/`,
            {
                baslik,
                alt_baslik: altBaslik,
                seed: seed ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            },
            { responseType: "blob" },
        )
        .then((r) => r.data);

/** Alt durum (detaylı dosya akışı) */
export const dosyaDurumuRules = (dosyaTipi) =>
    api
        .get(`/v1/admin/operasyon/alt-durum/rules/`, {
            params: dosyaTipi ? { dosya_tipi: dosyaTipi } : {},
        })
        .then((r) => r.data);

export const dosyaDurumuValidate = (payload) =>
    api.post(`/v1/admin/operasyon/alt-durum/validate/`, payload).then((r) => r.data);

export const dosyaDurumuTransition = (payload) =>
    api.post(`/v1/admin/operasyon/alt-durum/transition/`, payload).then((r) => r.data);

export const zamanAsimiDashboard = () =>
    api.get(`/v1/admin/operasyon/zaman-asimi/`).then((r) => r.data);

export const odemeRaporlari = (params = {}) =>
    api.get(`/v1/admin/operasyon/odeme-raporlari/`, { params }).then((r) => r.data);

export const odemeTakipOzet = (params = {}) =>
    api.get(`/v1/admin/operasyon/odeme-takip-ozet/`, { params }).then((r) => r.data);

export const muvekkilListeOzet = (params = {}) =>
    api.get(`/v1/admin/operasyon/muvekkil-liste-ozet/`, { params }).then((r) => r.data);

export const sigortaDosyaListeOzet = (params = {}) =>
    api.get(`/v1/admin/operasyon/sigorta-dosya-liste-ozet/`, { params }).then((r) => r.data);

export const sigortaDosyaKartOzet = (id) =>
    api.get(`/v1/admin/sigorta-dosyalari/${id}/kart-ozet/`).then((r) => r.data);

export const muvekkilKartOzet = (id) =>
    api.get(`/v1/admin/muvekkiller/${id}/kart-ozet/`).then((r) => r.data);

export const araciListeOzet = (params = {}) =>
    api.get(`/v1/admin/operasyon/araci-liste-ozet/`, { params }).then((r) => r.data);

export const kasaOzet = () =>
    api.get(`/v1/admin/operasyon/kasa-ozet/`).then((r) => r.data);

export const hesapTransfer = (payload) =>
    api.post(`/v1/admin/operasyon/hesap-transfer/`, payload).then((r) => r.data);

export const yetkiProfilim = () =>
    api.get(`/v1/admin/operasyon/yetki-profilim/`).then((r) => r.data);

/** Sistem sağlığı — Redis, Celery, MySQL yedek (salt okunur) */
export const sistemSagligi = () =>
    api.get(`/v1/admin/operasyon/sistem-sagligi/`).then((r) => r.data);

export const yetkiSablonSeed = () =>
    api.post(`/v1/admin/operasyon/yetki-sablon-seed/`).then((r) => r.data);

export const kullaniciYetkiAta = (payload) =>
    api.post(`/v1/admin/operasyon/kullanici-yetki/`, payload).then((r) => r.data);

/** Canlı panel bildirimleri (polling) */
export const liveNotifications = (since) =>
    api
        .get(`/v1/admin/canli-bildirimler/`, {
            params: since ? { since } : {},
        })
        .then((r) => r.data);

export const systemActivity = () =>
    api.get(`/v1/admin/sistem-aktivite/`).then((r) => r.data);

export const deleteNotification = (id) =>
    api.delete(`/v1/admin/canli-bildirimler/${id}/`).then((r) => r.data);

export const clearNotifications = () =>
    api.post(`/v1/admin/canli-bildirimler/temizle/`).then((r) => r.data);

/** Operasyon Hub — finans P&L, sağlık, DK kalibrasyon, workflow pilot, pazarlık */
export const operasyonHub = {
    finans: (params = {}) =>
        api.get(`/v1/admin/operasyon/hub/finans/`, { params }).then((r) => r.data),
    saglik: (params = {}) =>
        api.get(`/v1/admin/operasyon/hub/saglik/`, { params }).then((r) => r.data),
    dkKalibrasyon: () =>
        api.get(`/v1/admin/operasyon/hub/dk-kalibrasyon/`).then((r) => r.data),
    workflowPilot: (payload) =>
        api.post(`/v1/admin/operasyon/hub/workflow-pilot/`, payload).then((r) => r.data),
    pazarlik: (params = {}) =>
        api.get(`/v1/admin/operasyon/hub/pazarlik/`, { params }).then((r) => r.data),
};

/** Filo canlı destek chat (admin) */
export const filoChat = {
    listRooms: () => api.get(`/v1/admin/filo-chat/odalar/`).then((r) => r.data),
    getMessages: (odaId) =>
        api.get(`/v1/admin/filo-chat/odalar/${odaId}/mesajlar/`).then((r) => r.data),
    send: (odaId, mesaj) =>
        api.post(`/v1/admin/filo-chat/odalar/${odaId}/gonder/`, { mesaj }).then((r) => r.data),
    uploadFile: (odaId, file, mesaj = "") => {
        const fd = new FormData();
        fd.append("dosya", file);
        if (mesaj) fd.append("mesaj", mesaj);
        return api
            .post(`/v1/admin/filo-chat/odalar/${odaId}/dosya/`, fd, {
                timeout: 120000,
            })
            .then((r) => r.data);
    },
    unread: () => api.get(`/v1/admin/filo-chat/okunmamis/`).then((r) => r.data),
    clearRoom: (odaId) =>
        api.post(`/v1/admin/filo-chat/odalar/${odaId}/temizle/`).then((r) => r.data),
};

/** Aracı bildirim şablonları ve toplu gönderim */
export const araciMerkez = {
    dashboard: (params = {}) =>
        api.get(`/v1/admin/araci-merkezi/dashboard/`, { params }).then((r) => r.data),
    leads: (params = {}) =>
        api.get(`/v1/admin/araci-merkezi/leads/`, { params }).then((r) => r.data),
    leadOnizle: (leadId) =>
        api.get(`/v1/admin/araci-merkezi/leads/${leadId}/onizle/`).then((r) => r.data),
    leadOnayla: (leadId, payload = {}) =>
        api.post(`/v1/admin/araci-merkezi/leads/${leadId}/onayla/`, payload).then((r) => r.data),
    leadReddet: (leadId, sebep) =>
        api.post(`/v1/admin/araci-merkezi/leads/${leadId}/reddet/`, { sebep }).then((r) => r.data),
    hakedisOdendi: (hakedisId) =>
        api.post(`/v1/admin/araci-merkezi/hakedis/${hakedisId}/odendi/`).then((r) => r.data),
    ayarlar: () => api.get(`/v1/admin/araci-merkezi/ayarlar/`).then((r) => r.data),
    ayarlarKaydet: (payload) =>
        api.patch(`/v1/admin/araci-merkezi/ayarlar/`, payload).then((r) => r.data),
    topluEmail: (payload) =>
        api.post(`/v1/admin/araci-merkezi/ayarlar/toplu-email/`, payload).then((r) => r.data),
};

export const araciBildirim = {
    templates: () => api.get(`/v1/admin/araci-bildirimler/sablonlar/`).then((r) => r.data),
    send: (payload) =>
        api.post(`/v1/admin/araci-bildirimler/gonder/`, payload).then((r) => r.data),
    ozetPdf: (araciId) =>
        api.get(`/v1/admin/araci-bildirimler/ozet-pdf/`, {
            params: araciId ? { araci: araciId } : {},
            responseType: "blob",
        }),
};


/** E-posta Merkezi — IMAP sync, parse, eşleme */
export const epostaMerkez = {
    ozet: () => api.get(`/v1/admin/eposta-merkezi/ozet/`).then((r) => r.data),
    list: (status, mailbox, q) =>
        api
            .get(`/v1/admin/eposta-merkezi/`, {
                params: {
                    ...(status && status !== "takip" ? { status } : {}),
                    ...(mailbox && mailbox !== "all" ? { mailbox } : {}),
                    ...(q ? { q } : {}),
                    limit: 80,
                },
                timeout: 90000,
            })
            .then((r) => r.data),
    create: (payload) => api.post(`/v1/admin/eposta-merkezi/`, payload).then((r) => r.data),
    match: (id, dosyaId, dosyaNo) => {
        const payload = {};
        if (dosyaId) payload.dosya_id = dosyaId;
        if (dosyaNo) payload.dosya_no = dosyaNo;
        return api.post(`/v1/admin/eposta-merkezi/${id}/match/`, payload).then((r) => r.data);
    },
    apply: (id, dosyaId) =>
        api
            .post(`/v1/admin/eposta-merkezi/${id}/apply/`, {
                dosya_id: dosyaId || undefined,
            })
            .then((r) => r.data),
    imapSync: (provider = "all") =>
        api
            .post(`/v1/admin/eposta-merkezi/imap-sync/`, { provider, limit: 50 }, { timeout: 180000 })
            .then((r) => r.data),
    oauthTamamla: () => api.post(`/v1/admin/eposta-merkezi/oauth-tamamla/`, {}, { timeout: 300000 }).then((r) => r.data),
    azureClientGet: () => api.get(`/v1/admin/eposta-merkezi/azure-client/`).then((r) => r.data),
    azureClientSet: (clientId, tenantId) =>
        api
            .post(`/v1/admin/eposta-merkezi/azure-client/`, {
                client_id: clientId,
                tenant_id: tenantId || undefined,
            })
            .then((r) => r.data),
    oauthBaslat: () => api.post(`/v1/admin/eposta-merkezi/oauth-baslat/`, {}, { timeout: 60000 }).then((r) => r.data),
    syncAyarGet: () => api.get(`/v1/admin/eposta-merkezi/sync-ayar/`).then((r) => r.data),
    sifirla: () => api.post(`/v1/admin/eposta-merkezi/sifirla/`).then((r) => r.data),
    syncAyarSet: (enabled) => api.post(`/v1/admin/eposta-merkezi/sync-ayar/`, { enabled }).then((r) => r.data),
    takipKayitlari: () =>
        api.get(`/v1/admin/eposta-merkezi/takip-kayitlari/`).then((r) => r.data),
    sirketKurallari: () =>
        api.get(`/v1/admin/eposta-merkezi/sirket-kurallari/`).then((r) => r.data),
    suggest: (id) =>
        api.post(`/v1/admin/eposta-merkezi/${id}/suggest/`).then((r) => r.data),
    onaylaBagla: (id, dosyaId, dosyaNo) => {
        const payload = {};
        if (dosyaId) payload.dosya_id = dosyaId;
        if (dosyaNo) payload.dosya_no = dosyaNo;
        return api.post(`/v1/admin/eposta-merkezi/${id}/onayla-bagla/`, payload).then((r) => r.data);
    },
    detay: (id) => api.get(`/v1/admin/eposta-merkezi/${id}/detay/`).then((r) => r.data),
    dosyaAra: (q) => api.get("/v1/admin/eposta-merkezi/dosya-ara/", { params: { q } }).then((r) => r.data),
    dosyaEpostalar: (dosyaId) =>
        api.get(`/v1/admin/eposta-merkezi/dosya/${dosyaId}/epostalar/`).then((r) => r.data),
    dashboard: () => api.get(`/v1/admin/eposta-merkezi/dashboard/`).then((r) => r.data),
    raporGonder: () => api.post(`/v1/admin/eposta-merkezi/rapor-gonder/`).then((r) => r.data),
    akilAnaliz: (epostaId) =>
        api.post(`/v1/admin/eposta-merkezi/akil-analiz/`, { eposta_id: epostaId }, { timeout: 120000 }).then((r) => r.data),
    sirketKuralOlustur: (payload) =>
        api.post(`/v1/admin/eposta-merkezi/sirket-kurallari/`, payload).then((r) => r.data),
    sirketKuralGuncelle: (id, payload) =>
        api.patch(`/v1/admin/eposta-merkezi/sirket-kurallari/${id}/`, payload).then((r) => r.data),
    sirketKuralSil: (id) =>
        api.delete(`/v1/admin/eposta-merkezi/sirket-kurallari/${id}/`).then((r) => r.data),
    gidenOzet: () => api.get(`/v1/admin/eposta-merkezi/giden/ozet/`).then((r) => r.data),
    gidenRetry: (id) => api.post(`/v1/admin/eposta-merkezi/giden/${id}/retry/`).then((r) => r.data),
    gidenList: (params) =>
        api.get(`/v1/admin/gonderilecek-epostalar/`, { params }).then((r) => {
            const d = r.data;
            return Array.isArray(d) ? d : d.results || d.items || [];
        }),
    gidenOlustur: (payload) => api.post(`/v1/admin/gonderilecek-epostalar/`, payload).then((r) => r.data),
    sablonList: () =>
        api.get(`/v1/admin/eposta-sablonlari/`).then((r) => {
            const d = r.data;
            return Array.isArray(d) ? d : d.results || d.items || [];
        }),
    sablonOlustur: (payload) => api.post(`/v1/admin/eposta-sablonlari/`, payload).then((r) => r.data),
    sablonGuncelle: (id, payload) =>
        api.patch(`/v1/admin/eposta-sablonlari/${id}/`, payload).then((r) => r.data),
};

/** Gelen e-posta parse / eşleme */
export const incomingEmails = (status) =>
    api
        .get(`/v1/admin/gelen-epostalar/`, {
            params: status ? { status } : {},
        })
        .then((r) => r.data);

export const ingestIncomingEmail = (payload) =>
    api.post(`/v1/admin/gelen-epostalar/`, payload).then((r) => r.data);

export const matchIncomingEmail = (id, dosyaId, dosyaNo) => {
    const payload = {};
    if (dosyaId) payload.dosya_id = dosyaId;
    if (dosyaNo) payload.dosya_no = dosyaNo;
    return api
        .post(`/v1/admin/gelen-epostalar/${id}/match/`, payload)
        .then((r) => r.data);
};

export const applyIncomingEmail = (id, dosyaId) =>
    api
        .post(`/v1/admin/gelen-epostalar/${id}/apply/`, {
            dosya_id: dosyaId || undefined,
        })
        .then((r) => r.data);

/** SMS / WhatsApp şablonları */
export const messageTemplates = () =>
    api.get(`/v1/admin/mesaj-sablonlari/`).then((r) => r.data);

export const sendMessage = (payload) =>
    api.post(`/v1/admin/mesaj-gonder/`, payload).then((r) => r.data);

/** Takvim Merkezi — birleşik etkinlik API */
export const takvimMerkez = {
    list: (params = {}) =>
        api.get(`/v1/admin/takvim-merkez/`, { params }).then((r) => r.data),
    yaklasan: () =>
        api.get(`/v1/admin/takvim-merkez/yaklasan/`).then((r) => r.data),
    olustur: (payload) =>
        api.post(`/v1/admin/takvim-merkez/olustur/`, payload).then((r) => r.data),
    guncelle: (source, id, payload) =>
        api.patch(`/v1/admin/takvim-merkez/${source}/${id}/`, payload).then((r) => r.data),
    sil: (source, id) =>
        api.delete(`/v1/admin/takvim-merkez/${source}/${id}/`).then((r) => r.data),
};

/** WhatsApp Merkezi — otomasyon, şablon, kuyruk */
export const whatsappMerkez = {
    ozet: () => api.get(`/v1/admin/whatsapp-merkez/ozet/`).then((r) => r.data),
    ayarlar: () => api.get(`/v1/admin/whatsapp-merkez/ayarlar/`).then((r) => r.data),
    ayarlarKaydet: (payload) =>
        api.patch(`/v1/admin/whatsapp-merkez/ayarlar/`, payload).then((r) => r.data),
    baglanti: () => api.get(`/v1/admin/whatsapp-merkez/baglanti/`).then((r) => r.data),
    sablonlar: () => api.get(`/v1/admin/whatsapp-merkez/sablonlar/`).then((r) => r.data),
    sablonOlustur: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/sablonlar/`, payload).then((r) => r.data),
    sablonGuncelle: (id, payload) =>
        api.patch(`/v1/admin/whatsapp-merkez/sablonlar/${id}/`, payload).then((r) => r.data),
    sablonSil: (id) =>
        api.delete(`/v1/admin/whatsapp-merkez/sablonlar/${id}/`).then((r) => r.data),
    sablonSeed: () =>
        api.post(`/v1/admin/whatsapp-merkez/sablonlar/seed/`).then((r) => r.data),
    bildirimMerkezi: () =>
        api.get(`/v1/admin/whatsapp-merkez/bildirim-merkezi/`).then((r) => r.data),
    bildirimToplu: (payload) =>
        api.patch(`/v1/admin/whatsapp-merkez/bildirim-merkezi/toplu/`, payload).then((r) => r.data),
    bildirimTest: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/bildirim-merkezi/test/`, payload).then((r) => r.data),
    bildirimKutusu: (since, kategori) =>
        api
            .get(`/v1/admin/whatsapp-merkez/bildirim-kutusu/`, {
                params: {
                    ...(since ? { since } : {}),
                    ...(kategori ? { kategori } : {}),
                },
            })
            .then((r) => r.data),
    bildirimKutusuTemizle: () =>
        api.post(`/v1/admin/whatsapp-merkez/bildirim-kutusu/temizle/`).then((r) => r.data),
    modulMerkezi: (gun = 7) =>
        api.get(`/v1/admin/whatsapp-merkez/modul-merkezi/`, { params: { gun } }).then((r) => r.data),
    modulMerkeziKaydet: (payload) =>
        api.patch(`/v1/admin/whatsapp-merkez/modul-merkezi/kaydet/`, payload).then((r) => r.data),
    modulTopluKapat: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/modul-merkezi/toplu-kapat/`, payload).then((r) => r.data),
    onizleme: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/sablon-onizleme/`, payload).then((r) => r.data),
    gonderimler: (params) =>
        api
            .get(`/v1/admin/whatsapp-merkez/gonderimler/`, { params })
            .then((r) => r.data),
    gonderimOlustur: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/gonderimler/`, payload).then((r) => r.data),
    gonder: (id) =>
        api
            .post(`/v1/admin/whatsapp-merkez/gonderimler/${id}/gonder/`, { async: true }, { timeout: 20000 })
            .then((r) => r.data),
    onayla: (id) =>
        api
            .post(`/v1/admin/whatsapp-merkez/gonderimler/${id}/onayla/`, {}, { timeout: 20000 })
            .then((r) => r.data),
    iptal: (id) =>
        api
            .post(`/v1/admin/whatsapp-merkez/gonderimler/${id}/iptal/`, {}, { timeout: 20000 })
            .then((r) => r.data),
    isaretle: (id) =>
        api
            .post(`/v1/admin/whatsapp-merkez/gonderimler/${id}/isaretle/`, {}, { timeout: 20000 })
            .then((r) => r.data),
    kuyrukIsle: (limit = 20) =>
        api
            .post(`/v1/admin/whatsapp-merkez/gonderimler/kuyruk-isle/`, { limit })
            .then((r) => r.data),
    tahkimAdaylari: () =>
        api.get(`/v1/admin/whatsapp-merkez/tahkim-adaylari/`).then((r) => r.data),
    tahkimSync: () =>
        api.post(`/v1/admin/whatsapp-merkez/tahkim-adaylari/sync/`).then((r) => r.data),
    tahkimGonder: (payload) =>
        api
            .post(`/v1/admin/whatsapp-merkez/tahkim-adaylari/gonder/`, payload, { timeout: 20000 })
            .then((r) => r.data),
    lookupMuvekkiller: (q) =>
        api
            .get(`/v1/admin/whatsapp-merkez/lookup/muvekkiller/`, { params: { q } })
            .then((r) => r.data),
    lookupDosyalar: (params) =>
        api
            .get(`/v1/admin/whatsapp-merkez/lookup/dosyalar/`, { params })
            .then((r) => r.data),
    istatistik: () =>
        api.get(`/v1/admin/whatsapp-merkez/istatistik/`).then((r) => r.data),
    degiskenler: () =>
        api.get(`/v1/admin/whatsapp-merkez/degiskenler/`).then((r) => r.data),
    testGonder: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/test-gonder/`, payload).then((r) => r.data),
    raporPdfUrl: () => `/api/v1/admin/whatsapp-merkez/rapor-pdf/`,
    raporPdfIndir: () =>
        api
            .get(`/v1/admin/whatsapp-merkez/rapor-pdf/`, { responseType: "blob" })
            .then((r) => {
                const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = "whatsapp-haftalik-rapor.pdf";
                a.click();
                window.URL.revokeObjectURL(url);
            }),
    dosyaGecmisi: (dosyaId) =>
        api
            .get(`/v1/admin/whatsapp-merkez/dosyalar/${dosyaId}/gecmis/`)
            .then((r) => r.data),
    dosyaWaOzet: (dosyaId) =>
        api
            .get(`/v1/admin/whatsapp-merkez/dosyalar/${dosyaId}/wa-ozet/`)
            .then((r) => r.data),
    dosyaYatarYeniden: (dosyaId) =>
        api
            .post(`/v1/admin/whatsapp-merkez/dosyalar/${dosyaId}/yatar-yeniden/`)
            .then((r) => r.data),
    otomasyonCalistir: () =>
        api.post(`/v1/admin/whatsapp-merkez/otomasyon/calistir/`).then((r) => r.data),
    inbox: (params) =>
        api.get(`/v1/admin/whatsapp-merkez/inbox/`, { params }).then((r) => r.data),
    inboxKonusmalar: (params) =>
        api.get(`/v1/admin/whatsapp-merkez/inbox/`, { params: { gruplu: 1, ...params } }).then((r) => r.data),
    inboxKonusmalarGelismis: (params) =>
        api.get(`/v1/admin/whatsapp-merkez/inbox/konusmalar/`, { params }).then((r) => r.data),
    inboxKonusmaMetaGet: (konusmaAnahtar) =>
        api
            .get(`/v1/admin/whatsapp-merkez/inbox/konusma/meta/`, {
                params: { konusma_anahtar: konusmaAnahtar },
            })
            .then((r) => r.data),
    inboxKonusmaMetaPatch: (konusmaAnahtar, payload) =>
        api
            .patch(`/v1/admin/whatsapp-merkez/inbox/konusma/meta/`, {
                konusma_anahtar: konusmaAnahtar,
                ...payload,
            })
            .then((r) => r.data),
    inboxCrm: (konusmaAnahtar) =>
        api
            .get(`/v1/admin/whatsapp-merkez/inbox/konusma/crm/`, {
                params: { konusma_anahtar: konusmaAnahtar },
            })
            .then((r) => r.data),
    inboxCrmBagla: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/konusma/crm/bagla/`, payload).then((r) => r.data),
    inboxCrmGorevOlustur: (konusmaAnahtar, payload) =>
        api
            .post(`/v1/admin/whatsapp-merkez/inbox/konusma/crm/gorevler/`, {
                konusma_anahtar: konusmaAnahtar,
                ...payload,
            })
            .then((r) => r.data),
    inboxCrmGorevGuncelle: (gorevId, payload) =>
        api
            .patch(`/v1/admin/whatsapp-merkez/inbox/konusma/crm/gorevler/${gorevId}/`, payload)
            .then((r) => r.data),
    inboxCrmGorevSil: (gorevId) =>
        api.delete(`/v1/admin/whatsapp-merkez/inbox/konusma/crm/gorevler/${gorevId}/`).then((r) => r.data),
    inboxVekaletOrnek: () =>
        api.get(`/v1/admin/whatsapp-merkez/inbox/vekalet-ornek/`).then((r) => r.data),
    inboxVekaletOrnekYukle: (file) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.post(`/v1/admin/whatsapp-merkez/inbox/vekalet-ornek/`, fd).then((r) => r.data);
    },
    inboxVekaletOrnekUret: () =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/vekalet-ornek/`).then((r) => r.data),
    inboxVekaletGonder: (konusmaAnahtar, mesaj = null) =>
        api
            .post(`/v1/admin/whatsapp-merkez/inbox/vekalet-gonder/`, {
                konusma_anahtar: konusmaAnahtar,
                ...(mesaj ? { mesaj } : {}),
            })
            .then((r) => r.data),
    inboxBotTest: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/bot-test/`, payload).then((r) => r.data),
    inboxBotAnalitik: (gun = 7) =>
        api.get(`/v1/admin/whatsapp-merkez/inbox/bot-analitik/`, { params: { gun } }).then((r) => r.data),
    inboxAra: (q, limit = 50) =>
        api.get(`/v1/admin/whatsapp-merkez/inbox/ara/`, { params: { q, limit } }).then((r) => r.data),
    inboxKullanicilar: () =>
        api.get(`/v1/admin/whatsapp-merkez/inbox/kullanicilar/`).then((r) => r.data),
    inboxSablonGonder: (konusmaAnahtar, sablon_kod) =>
        api
            .post(`/v1/admin/whatsapp-merkez/inbox/sablon-gonder/`, {
                konusma_anahtar: konusmaAnahtar,
                sablon_kod,
            })
            .then((r) => r.data),
    inboxSablonOnizle: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/sablon-onizle/`, payload).then((r) => r.data),
    inboxBotAkislari: () =>
        api.get(`/v1/admin/whatsapp-merkez/inbox/bot-akislari/`).then((r) => r.data),
    inboxBotAkisKaydet: (flow) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/bot-akislari/`, flow).then((r) => r.data),
    inboxBotAkisGuncelle: (id, payload) =>
        api.patch(`/v1/admin/whatsapp-merkez/inbox/bot-akislari/${id}/`, payload).then((r) => r.data),
    inboxSil: (ids) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/sil/`, { ids }).then((r) => r.data),
    inboxKonusmaSil: (telefon) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/konusma-sil/`, { telefon }).then((r) => r.data),
    inboxEslestirmeKaldir: (telefon, gorunen_ad = "") =>
        api
            .post(`/v1/admin/whatsapp-merkez/inbox/eslestirme-kaldir/`, { telefon, gorunen_ad })
            .then((r) => r.data),
    inboxEtiket: (telefon, gorunen_ad, muvekkil_id = null) =>
        api
            .post(`/v1/admin/whatsapp-merkez/inbox/etiket/`, {
                telefon,
                gorunen_ad,
                ...(muvekkil_id ? { muvekkil_id } : {}),
            })
            .then((r) => r.data),
    inboxKonusmaOkundu: (telefon) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/konusma-okundu/`, { telefon }).then((r) => r.data),
    inboxKonusmaYanit: (telefon, mesaj) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/konusma-yanit/`, { telefon, mesaj }).then((r) => r.data),
    inboxOkundu: (id) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/${id}/okundu/`).then((r) => r.data),
    inboxYanit: (id, mesaj) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/${id}/yanit/`, { mesaj }).then((r) => r.data),
    inboxBagla: (id, payload) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/${id}/bagla/`, payload).then((r) => r.data),
    inboxEvrakYukle: (id, payload = {}) => {
        if (payload.file instanceof File || payload.file instanceof Blob) {
            const fd = new FormData();
            if (payload.dosya_id) fd.append("dosya_id", String(payload.dosya_id));
            fd.append("evrak", payload.file, payload.file.name || "whatsapp-evrak.pdf");
            return api
                .post(`/v1/admin/whatsapp-merkez/inbox/${id}/evrak-yukle/`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                })
                .then((r) => r.data);
        }
        return api.post(`/v1/admin/whatsapp-merkez/inbox/${id}/evrak-yukle/`, payload).then((r) => r.data);
    },
    inboxOnayKuyrugu: (params) =>
        api.get(`/v1/admin/whatsapp-merkez/inbox/onay-kuyrugu/`, { params }).then((r) => r.data),
    inboxEslestirmeOnizle: (id) =>
        api.get(`/v1/admin/whatsapp-merkez/inbox/${id}/eslestirme-onizle/`).then((r) => r.data),
    inboxOnayla: (id, payload = {}) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/${id}/onayla/`, payload).then((r) => r.data),
    inboxReddet: (id, sebep = "") =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/${id}/reddet/`, { sebep }).then((r) => r.data),
    inboxBekleyenMedyaUrl: (id) =>
        `${API_BASE_URL}/v1/admin/whatsapp-merkez/inbox/${id}/bekleyen-medya/`,
    inboxBekleyenMedyaBlob: (id, opts = {}) =>
        api
            .get(`/v1/admin/whatsapp-merkez/inbox/${id}/bekleyen-medya/`, {
                responseType: "blob",
                timeout: 30000,
                params: opts?.retry ? { retry: 1 } : undefined,
            })
            .then((r) => r.data),
    inboxKonusmaVarsayilanGet: (konusmaAnahtar) =>
        api
            .get(`/v1/admin/whatsapp-merkez/inbox/konusma/varsayilan-dosya/`, {
                params: { konusma_anahtar: konusmaAnahtar },
            })
            .then((r) => r.data),
    inboxKonusmaVarsayilanSet: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/konusma/varsayilan-dosya/`, payload).then((r) => r.data),
    evrakEksik: (params) =>
        api.get(`/v1/admin/whatsapp-merkez/evrak-eksik/`, { params }).then((r) => r.data),
    evrakGonder: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/evrak-gonder/`, payload).then((r) => r.data),
    kampanyalar: () =>
        api.get(`/v1/admin/whatsapp-merkez/kampanyalar/`).then((r) => r.data),
    kampanyaOlustur: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/kampanyalar/`, payload).then((r) => r.data),
    kampanyaOnizle: (filtre) =>
        api.post(`/v1/admin/whatsapp-merkez/kampanyalar/`, { onizleme: true, filtre }).then((r) => r.data),
    saglik: () => api.get(`/v1/admin/whatsapp-merkez/saglik/`).then((r) => r.data),
    dashboard: () => api.get(`/v1/admin/whatsapp-merkez/dashboard/`).then((r) => r.data),
    raporGonder: () =>
        api.post(`/v1/admin/whatsapp-merkez/rapor-gonder/`).then((r) => r.data),
    optOutList: () => api.get(`/v1/admin/whatsapp-merkez/opt-out/`).then((r) => r.data),
    optOutEkle: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/opt-out/`, payload).then((r) => r.data),
    optOutSil: (id) =>
        api.delete(`/v1/admin/whatsapp-merkez/opt-out/${id}/`).then((r) => r.data),
    retryHatalar: (limit = 20) =>
        api.post(`/v1/admin/whatsapp-merkez/retry-hatalar/`, { limit }).then((r) => r.data),
    durumGecisleri: () =>
        api.get(`/v1/admin/whatsapp-merkez/durum-gecisleri/`).then((r) => r.data),
    lifecycleCleanup: (dryRun = false) =>
        api
            .post(`/v1/admin/whatsapp-merkez/lifecycle-cleanup/`, { dry_run: dryRun })
            .then((r) => r.data),
    inboxAkilTaslak: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/inbox/akil-taslak/`, payload).then((r) => r.data),
    kurallar: () => api.get(`/v1/admin/whatsapp-merkez/kurallar/`).then((r) => r.data),
    kuralOlustur: (payload) =>
        api.post(`/v1/admin/whatsapp-merkez/kurallar/`, payload).then((r) => r.data),
    kuralGuncelle: (id, payload) =>
        api.patch(`/v1/admin/whatsapp-merkez/kurallar/${id}/`, payload).then((r) => r.data),
    kuralSil: (id) =>
        api.delete(`/v1/admin/whatsapp-merkez/kurallar/${id}/`).then((r) => r.data),
    kurallarCalistir: (limit = 200) =>
        api.post(`/v1/admin/whatsapp-merkez/kurallar/calistir/`, { limit }).then((r) => r.data),
    yatarOnayOzet: () =>
        api.get(`/v1/admin/whatsapp-merkez/yatar-onay/ozet/`).then((r) => r.data),
    yatarOnayListe: (params) =>
        api.get(`/v1/admin/whatsapp-merkez/yatar-onay/`, { params }).then((r) => r.data),
    yatarOnayDenetim: (kayitId) =>
        api.get(`/v1/admin/whatsapp-merkez/yatar-onay/${kayitId}/denetim/`).then((r) => r.data),
    yatarOnayPanelOnay: (kayitId) =>
        api.post(`/v1/admin/whatsapp-merkez/yatar-onay/${kayitId}/panel-onay/`).then((r) => r.data),
    yatarOnayPanelRed: (kayitId, payload = {}) =>
        api.post(`/v1/admin/whatsapp-merkez/yatar-onay/${kayitId}/panel-red/`, payload).then((r) => r.data),
    yatarOnaySil: (kayitId) =>
        api.delete(`/v1/admin/whatsapp-merkez/yatar-onay/${kayitId}/`).then((r) => r.data),
    yatarOnayTest: (payload = {}) =>
        api.post(`/v1/admin/whatsapp-merkez/yatar-onay/test/`, payload).then((r) => r.data),
    yatarOnayHatirlat: (kayitId) =>
        api.post(`/v1/admin/whatsapp-merkez/yatar-onay/${kayitId}/hatirlat/`).then((r) => r.data),
    yatarDosyaTimeline: (dosyaId) =>
        api.get(`/v1/admin/whatsapp-merkez/dosyalar/${dosyaId}/yatar-timeline/`).then((r) => r.data),
};

/** Müvekkil Merkezi — portal operasyon hub */
export const muvekkilMerkez = {
    ozet: () => api.get(`/v1/admin/operasyon/muvekkil-merkez/ozet/`).then((r) => r.data),
    onayKuyrugu: (params = {}) =>
        api
            .get(`/v1/admin/operasyon/muvekkil-merkez/onay-kuyrugu/`, { params })
            .then((r) => r.data),
    mesajlar: (params = {}) =>
        api.get(`/v1/admin/operasyon/muvekkil-merkez/mesajlar/`, { params }).then((r) => r.data),
    mesajSohbetler: (params = {}) =>
        api
            .get(`/v1/admin/operasyon/muvekkil-merkez/mesaj-sohbetler/`, { params })
            .then((r) => r.data),
    mesajSohbet: (dosyaId) =>
        api
            .get(`/v1/admin/operasyon/muvekkil-merkez/mesaj-sohbet/`, {
                params: { dosya_id: dosyaId },
            })
            .then((r) => r.data),
    aktivite: (params = {}) =>
        api.get(`/v1/admin/operasyon/muvekkil-merkez/aktivite/`, { params }).then((r) => r.data),
    eksikEvrak: (params = {}) =>
        api.get(`/v1/admin/operasyon/muvekkil-merkez/eksik-evrak/`, { params }).then((r) => r.data),
    evrakOnayla: (id) =>
        api
            .post(`/v1/admin/operasyon/muvekkil-merkez/evrak/${id}/onayla/`)
            .then((r) => r.data),
    evrakReddet: (id, red_nedeni) =>
        api
            .post(`/v1/admin/operasyon/muvekkil-merkez/evrak/${id}/reddet/`, { red_nedeni })
            .then((r) => r.data),
    mesajYanit: (payload) =>
        api.post(`/v1/admin/operasyon/muvekkil-merkez/mesaj-yanit/`, payload).then((r) => r.data),
    portalAkil: (params = {}) =>
        api.get(`/v1/admin/operasyon/muvekkil-merkez/portal-akil/`, { params }).then((r) => r.data),
    portalAkilAyar: (aktif) =>
        api
            .patch(`/v1/admin/operasyon/muvekkil-merkez/portal-akil/`, { aktif })
            .then((r) => r.data),
    tahkimDosyalar: (params = {}) =>
        api
            .get(`/v1/admin/operasyon/muvekkil-merkez/tahkim-akil/dosyalar/`, { params })
            .then((r) => r.data),
    tahkimChecklist: (params = {}) =>
        api
            .get(`/v1/admin/operasyon/muvekkil-merkez/tahkim-akil/checklist/`, { params })
            .then((r) => r.data),
    tahkimDilekceOnizleme: (params = {}) =>
        api
            .get(`/v1/admin/operasyon/muvekkil-merkez/tahkim-akil/dilekce-onizleme/`, { params })
            .then((r) => r.data),
    tahkimBrifing: (payload) =>
        api
            .post(`/v1/admin/operasyon/muvekkil-merkez/tahkim-akil/brifing/`, payload)
            .then((r) => r.data),
    tahkimRedAnaliz: (payload) =>
        api
            .post(`/v1/admin/operasyon/muvekkil-merkez/tahkim-akil/red-analiz/`, payload)
            .then((r) => r.data),
    tahkimPaket: (payload) =>
        api
            .post(`/v1/admin/operasyon/muvekkil-merkez/tahkim-akil/paket/`, payload)
            .then((r) => r.data),
};

/** Filo Merkezi — kurumsal filo operasyon hub */
export const filoMerkez = {
    ozet: () => api.get(`/v1/admin/operasyon/filo-merkez/ozet/`).then((r) => r.data),
    hasarlar: (params = {}) =>
        api.get(`/v1/admin/operasyon/filo-merkez/hasarlar/`, { params }).then((r) => r.data),
    filolar: (params = {}) =>
        api.get(`/v1/admin/operasyon/filo-merkez/filolar/`, { params }).then((r) => r.data),
    aktivite: (params = {}) =>
        api.get(`/v1/admin/operasyon/filo-merkez/aktivite/`, { params }).then((r) => r.data),
    hasarDurum: (hasarId, durum) =>
        api
            .post(`/v1/admin/operasyon/filo-merkez/hasar/${hasarId}/durum/`, { durum })
            .then((r) => r.data),
    hasarDosyaBagla: (hasarId, sigortaDosyasiId) =>
        api
            .post(`/v1/admin/operasyon/filo-merkez/hasar/${hasarId}/dosya-bagla/`, {
                sigorta_dosyasi_id: sigortaDosyasiId,
            })
            .then((r) => r.data),
    hasarDosyaOlustur: (hasarId) =>
        api
            .post(`/v1/admin/operasyon/filo-merkez/hasar/${hasarId}/dosya-olustur/`)
            .then((r) => r.data),
    dosyaAday: (hasarId) =>
        api
            .get(`/v1/admin/operasyon/filo-merkez/hasar/${hasarId}/dosya-aday/`)
            .then((r) => r.data),
    sla: (params = {}) =>
        api.get(`/v1/admin/operasyon/filo-merkez/sla/`, { params }).then((r) => r.data),
    performans: (params = {}) =>
        api
            .get(`/v1/admin/operasyon/filo-merkez/performans/`, { params })
            .then((r) => r.data),
    analitik: (params = {}) =>
        api.get(`/v1/admin/operasyon/filo-merkez/analitik/`, { params }).then((r) => r.data),
    hasarDetay: (hasarId) =>
        api
            .get(`/v1/admin/operasyon/filo-merkez/hasar/${hasarId}/detay/`)
            .then((r) => r.data),
    dosyalar: (params = {}) =>
        api.get(`/v1/admin/operasyon/filo-merkez/dosyalar/`, { params }).then((r) => r.data),
    odemeler: (params = {}) =>
        api.get(`/v1/admin/operasyon/filo-merkez/odemeler/`, { params }).then((r) => r.data),
};

/** Rayiç İstihbarat Merkezi — nginx 300s ile uyumlu */
const RAYIC_TIMEOUT = 280000;

/** Rayiç İstihbarat Merkezi */
export const rayicIstihbarat = {
    ozet: () => api.get(`/v1/admin/operasyon/rayic-istihbarat/ozet/`).then((r) => r.data),
    arastir: (params = {}) =>
        api
            .get(`/v1/admin/operasyon/rayic-istihbarat/arastir/`, { params, timeout: RAYIC_TIMEOUT })
            .then((r) => r.data),
    trend: (params = {}) =>
        api.get(`/v1/admin/operasyon/rayic-istihbarat/trend/`, { params }).then((r) => r.data),
    uyarilar: (params = {}) =>
        api.get(`/v1/admin/operasyon/rayic-istihbarat/uyarilar/`, { params }).then((r) => r.data),
    dosyaSecim: (params = {}) =>
        api.get(`/v1/admin/operasyon/rayic-istihbarat/dosya-secim/`, { params }).then((r) => r.data),

    derinArastir: (payload) =>
        api
            .post(`/v1/admin/operasyon/rayic-istihbarat/derin-arastir/`, payload, { timeout: RAYIC_TIMEOUT })
            .then((r) => r.data),
    raporOnizle: (payload) =>
        api
            .post(`/v1/admin/operasyon/rayic-istihbarat/rapor-onizle/`, payload, { timeout: RAYIC_TIMEOUT })
            .then((r) => r.data),
    raporPdf: async (payload) => {
        const r = await api.post(`/v1/admin/operasyon/rayic-istihbarat/rapor-pdf/`, payload, {
            responseType: "blob",
            timeout: RAYIC_TIMEOUT,
        });
        const blob = r.data;
        if (blob?.type && blob.type.includes("json")) {
            const text = await blob.text();
            try {
                const json = JSON.parse(text);
                throw new Error(json.detail || json.error || "PDF oluşturulamadı");
            } catch (err) {
                if (err instanceof Error && !err.message.includes("PDF")) throw err;
                throw new Error(text.slice(0, 200) || "PDF oluşturulamadı");
            }
        }
        return blob;
    },
    segmentler: (params = {}) =>
        api.get(`/v1/admin/operasyon/rayic-istihbarat/segmentler/`, { params }).then((r) => r.data),
    dosyaGuncelle: (payload) =>
        api.post(`/v1/admin/operasyon/rayic-istihbarat/dosya-guncelle/`, payload).then((r) => r.data),
    parcaDosya: (dosyaId) =>
        api.get(`/v1/admin/operasyon/rayic-istihbarat/parca/dosya/${dosyaId}/`).then((r) => r.data),
    parcaArastir: (payload) =>
        api
            .post(`/v1/admin/operasyon/rayic-istihbarat/parca/arastir/`, payload, { timeout: RAYIC_TIMEOUT })
            .then((r) => r.data),
    parcaEkspertizAnaliz: (formData) =>
        api
            .post(`/v1/admin/operasyon/rayic-istihbarat/parca/ekspertiz-analiz/`, formData, {
                timeout: RAYIC_TIMEOUT,
            })
            .then((r) => r.data),
};

/** Evrak talep maili — sigorta dosyası detay */
export const evrakTalepMail = {
    sablonGet: () => api.get(`/v1/admin/operasyon/evrak-talep-mail/sablon/`).then((r) => r.data),
    sablonKaydet: (payload) =>
        api.post(`/v1/admin/operasyon/evrak-talep-mail/sablon/`, payload).then((r) => r.data),
    onizle: (dosyaId) =>
        api
            .get(`/v1/admin/operasyon/evrak-talep-mail/dosya/${dosyaId}/onizle/`)
            .then((r) => r.data),
    hazirla: (payload) =>
        api.post(`/v1/admin/operasyon/evrak-talep-mail/hazirla/`, payload).then((r) => r.data),
};

/** Bilus Parça Merkezi — bilus.com.tr oturumlu OEM/muadil arama */
export const bilusParca = {
    ozet: () => api.get(`/v1/admin/operasyon/bilus-parca/ozet/`).then((r) => r.data),
    ayarGet: () => api.get(`/v1/admin/operasyon/bilus-parca/ayar/`).then((r) => r.data),
    ayarKaydet: (payload) =>
        api.post(`/v1/admin/operasyon/bilus-parca/ayar/`, payload).then((r) => r.data),
    girisTest: () =>
        api.post(`/v1/admin/operasyon/bilus-parca/giris-test/`).then((r) => r.data),
    ara: (payload) =>
        api.post(`/v1/admin/operasyon/bilus-parca/ara/`, payload, { timeout: 120000 }).then((r) => r.data),
    fiyat: (oemId) =>
        api.get(`/v1/admin/operasyon/bilus-parca/fiyat/${oemId}/`).then((r) => r.data),
    esdeger: (oemId, marka) =>
        api
            .get(`/v1/admin/operasyon/bilus-parca/esdeger/${oemId}/`, { params: { marka } })
            .then((r) => r.data),
    dosya: (dosyaNo) =>
        api
            .get(`/v1/admin/operasyon/bilus-parca/dosya/${encodeURIComponent(dosyaNo)}/`)
            .then((r) => r.data),
    dosyaEvrak: (dosyaNo) =>
        api
            .get(`/v1/admin/operasyon/bilus-parca/dosya/${encodeURIComponent(dosyaNo)}/evrak/`)
            .then((r) => r.data),
    evrakTara: (payload) =>
        api
            .post(`/v1/admin/operasyon/bilus-parca/dosya-evrak-tara/`, payload, {
                timeout: 420000,
            })
            .then((r) => r.data),
    karsilastir: (payload) =>
        api
            .post(`/v1/admin/operasyon/bilus-parca/karsilastir/`, payload, { timeout: 280000 })
            .then((r) => r.data),
};

/** Partsouq OEM Karşılaştırma Merkezi */
export const partsouqOem = {
    ozet: () => api.get(`/v1/admin/operasyon/partsouq-oem/ozet/`).then((r) => r.data),
    dosya: (dosyaId) =>
        api.get(`/v1/admin/operasyon/partsouq-oem/dosya/${dosyaId}/`).then((r) => r.data),
    vinDecode: (payload) =>
        api.post(`/v1/admin/operasyon/partsouq-oem/vin-decode/`, payload).then((r) => r.data),
    karsilastir: (payload) =>
        api
            .post(`/v1/admin/operasyon/partsouq-oem/karsilastir/`, payload, { timeout: 280000 })
            .then((r) => r.data),
    ekspertizAnaliz: (formData) =>
        api
            .post(`/v1/admin/operasyon/partsouq-oem/ekspertiz-analiz/`, formData, { timeout: 280000 })
            .then((r) => r.data),
};

/** Ekspertiz & Hasar Merkezi — nginx 300s ile uyumlu timeout */
const EHM_TIMEOUT = 280000;

/** Ekspertiz & Hasar Merkezi */
export const ekspertizHasar = {
    ozet: () => api.get(`/v1/admin/operasyon/ekspertiz-hasar/ozet/`).then((r) => r.data),
    dosyalar: (params = {}) =>
        api.get(`/v1/admin/operasyon/ekspertiz-hasar/dosyalar/`, { params }).then((r) => r.data),
    dosyaDetay: (id) =>
        api.get(`/v1/admin/operasyon/ekspertiz-hasar/dosya/${id}/`).then((r) => r.data),
    analiz: (formData) =>
        api.post(`/v1/admin/operasyon/ekspertiz-hasar/analiz/`, formData, {
            timeout: EHM_TIMEOUT,
        }).then((r) => r.data),
    analizEvrak: (evrak_id, belge_tipi) =>
        api
            .post(`/v1/admin/operasyon/ekspertiz-hasar/analiz/`, { evrak_id, belge_tipi }, { timeout: EHM_TIMEOUT })
            .then((r) => r.data),
    karsilastir: (payload) =>
        api
            .post(`/v1/admin/operasyon/ekspertiz-hasar/karsilastir/`, payload, { timeout: EHM_TIMEOUT })
            .then((r) => r.data),
    dkOnizle: (payload) =>
        api
            .post(`/v1/admin/operasyon/ekspertiz-hasar/dk-onizle/`, payload, { timeout: EHM_TIMEOUT })
            .then((r) => r.data),
    dkAktar: (payload) =>
        api
            .post(`/v1/admin/operasyon/ekspertiz-hasar/dk-aktar/`, payload, { timeout: EHM_TIMEOUT })
            .then((r) => r.data),
    whatsappKuyruk: (params = {}) =>
        api.get(`/v1/admin/operasyon/ekspertiz-hasar/whatsapp-kuyruk/`, { params }).then((r) => r.data),
    gelismisUcgen: (payload) =>
        api
            .post(`/v1/admin/operasyon/ekspertiz-hasar/gelismis-ucgen/`, payload, { timeout: EHM_TIMEOUT })
            .then((r) => r.data),
    sunumRapor: (payload) =>
        api
            .post(`/v1/admin/operasyon/ekspertiz-hasar/sunum-rapor/`, payload, { timeout: EHM_TIMEOUT })
            .then((r) => r.data),
    adliPaket: (payload) =>
        api
            .post(`/v1/admin/operasyon/ekspertiz-hasar/adli-paket/`, payload, { timeout: EHM_TIMEOUT })
            .then((r) => r.data),
};

export const hasarSatinAlma = {
    ozet: () => api.get(`/v1/admin/operasyon/ekspertiz-hasar/satin-alma/ozet/`).then((r) => r.data),
    ruhsatAnaliz: (formData) =>
        api
            .post(`/v1/admin/operasyon/ekspertiz-hasar/satin-alma/ruhsat-analiz/`, formData, {
                timeout: EHM_TIMEOUT,
            })
            .then((r) => r.data),
    fotoAnaliz: (formData) =>
        api
            .post(`/v1/admin/operasyon/ekspertiz-hasar/satin-alma/foto-analiz/`, formData, {
                timeout: EHM_TIMEOUT,
            })
            .then((r) => r.data),
    teklifHesapla: (payload) =>
        api
            .post(`/v1/admin/operasyon/ekspertiz-hasar/satin-alma/teklif-hesapla/`, payload, {
                timeout: EHM_TIMEOUT,
            })
            .then((r) => r.data),
    tamAnaliz: (payload) =>
        api
            .post(`/v1/admin/operasyon/ekspertiz-hasar/satin-alma/teklif-hesapla/`, {
                ...payload,
                tam_akis: true,
            }, { timeout: EHM_TIMEOUT })
            .then((r) => r.data),
    teklifOnizle: (payload) =>
        api
            .post(`/v1/admin/operasyon/ekspertiz-hasar/satin-alma/teklif-onizle/`, payload, {
                timeout: EHM_TIMEOUT,
            })
            .then((r) => r.data),
    teklifPdf: async (payload) => {
        const r = await api.post(`/v1/admin/operasyon/ekspertiz-hasar/satin-alma/teklif-pdf/`, payload, {
            responseType: "blob",
            timeout: EHM_TIMEOUT,
        });
        const blob = r.data;
        if (blob?.type && blob.type.includes("json")) {
            const text = await blob.text();
            try {
                const json = JSON.parse(text);
                throw new Error(json.detail || json.error || "PDF oluşturulamadı");
            } catch (err) {
                if (err instanceof Error && !err.message.includes("PDF")) throw err;
                throw new Error(text.slice(0, 200) || "PDF oluşturulamadı");
            }
        }
        return blob;
    },
};

/** Gelişmiş Değer Kaybı Merkezi (bağımsız modül) */
export const dkGelismis = {
    diagram: () => api.get(`/v1/admin/operasyon/dk-gelismis/diagram/`).then((r) => r.data),
    dosyalar: (params = {}) =>
        api.get(`/v1/admin/operasyon/dk-gelismis/dosyalar/`, { params }).then((r) => r.data),
    dosya: (id) =>
        api.get(`/v1/admin/operasyon/dk-gelismis/dosya/${id}/`).then((r) => r.data),
    ekspertizForm: (id, params = {}) =>
        api
            .get(`/v1/admin/operasyon/dk-gelismis/dosya/${id}/ekspertiz-form/`, {
                params,
                timeout: 180000,
            })
            .then((r) => r.data),
    taslakYukle: (dosyaId) =>
        api
            .get(`/v1/admin/operasyon/dk-gelismis/taslak/`, {
                params: dosyaId ? { dosya_id: dosyaId } : {},
            })
            .then((r) => r.data),
    taslakKaydet: (payload) =>
        api.post(`/v1/admin/operasyon/dk-gelismis/taslak/`, payload).then((r) => r.data),
    rayic: (payload) =>
        api.post(`/v1/admin/operasyon/dk-gelismis/rayic/`, payload).then((r) => r.data),
    hesapla: (payload) =>
        api.post(`/v1/admin/operasyon/dk-gelismis/hesapla/`, payload).then((r) => r.data),
    kaydet: (payload) =>
        api.post(`/v1/admin/operasyon/dk-gelismis/kaydet/`, payload).then((r) => r.data),
    senaryo: (payload) =>
        api.post(`/v1/admin/operasyon/dk-gelismis/senaryo/`, payload).then((r) => r.data),
    ocr: (file, belgeTipi = "hasar_faturasi") => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("belge_tipi", belgeTipi);
        return api
            .post(`/v1/admin/operasyon/dk-gelismis/ocr/`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            })
            .then((r) => r.data);
    },
    ekspertizRapor: (file, opts = {}) => {
        const fd = new FormData();
        fd.append("file", file);
        if (opts.dosya_id) fd.append("dosya_id", String(opts.dosya_id));
        if (opts.piyasa_rayic_ara != null) fd.append("piyasa_rayic_ara", opts.piyasa_rayic_ara ? "1" : "0");
        if (opts.kusur_orani != null) fd.append("kusur_orani", String(opts.kusur_orani));
        if (opts.hasar_durumu) fd.append("hasar_durumu", opts.hasar_durumu);
        if (opts.vites_tip) fd.append("vites_tip", opts.vites_tip);
        if (opts.yakit_tip) fd.append("yakit_tip", opts.yakit_tip);
        if (opts.tramer_tutar != null) fd.append("tramer_tutar", String(opts.tramer_tutar));
        if (opts.km_min != null) fd.append("km_min", String(opts.km_min));
        if (opts.km_max != null) fd.append("km_max", String(opts.km_max));
        return api
            .post(`/v1/admin/operasyon/dk-gelismis/ekspertiz-raporu/`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 240000,
                validateStatus: (status) => status === 200 || status === 422,
            })
            .then((r) => r.data);
    },
    pdf: async (payload) => {
        const r = await api.post(`/v1/admin/operasyon/dk-gelismis/pdf/`, payload, {
            responseType: "blob",
            timeout: 120000,
        });
        const blob = r.data;
        if (blob?.type && blob.type !== "application/pdf") {
            const text = await blob.text();
            try {
                const json = JSON.parse(text);
                throw new Error(json.error || "PDF oluşturulamadı");
            } catch (err) {
                if (err instanceof Error && err.message !== "PDF oluşturulamadı") {
                    throw err;
                }
                throw new Error(text || "PDF oluşturulamadı");
            }
        }
        return blob;
    },
    whatsapp: (payload) =>
        api.post(`/v1/admin/operasyon/dk-gelismis/whatsapp/`, payload).then((r) => r.data),
    tahkimPaket: (payload) =>
        api.post(`/v1/admin/akil/tahkim-dk-paket/`, payload).then((r) => r.data),
    parcaEslestir: (parca_adi) =>
        api.post(`/v1/admin/operasyon/dk-gelismis/parca-eslestir/`, { parca_adi }).then((r) => r.data),
};

/** Aktüerya Merkezi — TRH-2010, progresif rant, PDF rapor */
export const aktueryaMerkez = {
    sabitler: () => api.get(`/v1/admin/operasyon/aktuerya/sabitler/`).then((r) => r.data),
    hesapla: (payload) => api.post(`/v1/admin/operasyon/aktuerya/hesapla/`, payload).then((r) => r.data),
    kaydet: (payload) => api.post(`/v1/admin/operasyon/aktuerya/kaydet/`, payload).then((r) => r.data),
    guncelle: (id, payload) =>
        api.patch(`/v1/admin/operasyon/aktuerya/detay/${id}/`, payload).then((r) => r.data),
    sil: (id) => api.delete(`/v1/admin/operasyon/aktuerya/detay/${id}/`).then((r) => r.data),
    liste: (params = {}) => api.get(`/v1/admin/operasyon/aktuerya/liste/`, { params }).then((r) => r.data),
    detay: (id) => api.get(`/v1/admin/operasyon/aktuerya/detay/${id}/`).then((r) => r.data),
    dosyaOnDoldur: (dosyaId, opts = {}) =>
        api
            .get(`/v1/admin/operasyon/aktuerya/dosya/${dosyaId}/on-doldur/`, {
                params: opts.ocr === false ? { ocr: "0" } : {},
            })
            .then((r) => r.data),
    senaryo: (payload) => api.post(`/v1/admin/operasyon/aktuerya/senaryo/`, payload).then((r) => r.data),
    pdf: async (payload) => {
        const r = await api.post(`/v1/admin/operasyon/aktuerya/pdf/`, payload, { responseType: "blob" });
        return r.data;
    },
};


export const yatarBedeli = {
    sabitler: () => api.get(`/v1/admin/operasyon/yatar-bedeli/sabitler/`).then((r) => r.data),
    dosyalar: (params = {}) =>
        api.get(`/v1/admin/operasyon/yatar-bedeli/dosyalar/`, { params }).then((r) => r.data),
    hesapla: (payload) => api.post(`/v1/admin/operasyon/yatar-bedeli/hesapla/`, payload).then((r) => r.data),
    liste: (params = {}) => api.get(`/v1/admin/operasyon/yatar-bedeli/liste/`, { params }).then((r) => r.data),
    detay: (id) => api.get(`/v1/admin/operasyon/yatar-bedeli/detay/${id}/`).then((r) => r.data),
    pdf: async (id) => {
        const r = await api.get(`/v1/admin/operasyon/yatar-bedeli/pdf/${id}/`, { responseType: "blob" });
        return r.data;
    },
};

/** Kusur Sorgulama Merkezi — AI kusur analizi, web araştırma, PDF rapor */
const KUSUR_TIMEOUT = 280000;

export async function pollAsyncJob(jobId, { interval = 1500, maxWait = 600000, onProgress, admin = true } = {}) {
    const url = admin
        ? `/v1/admin/operasyon/async-job/${jobId}/`
        : `/akil-job/${jobId}/`;
    const start = Date.now();
    while (Date.now() - start < maxWait) {
        const r = await api.get(url);
        const d = r.data;
        if (onProgress) onProgress(d);
        if (d.done) {
            if (d.status === "failed") throw new Error(d.error || d.message || "Job başarısız");
            const payload = d.result;
            if (payload && typeof payload === "object" && (payload.sonuc != null || payload.success != null || payload.form != null)) {
                return payload;
            }
            return payload || d;
        }
        await new Promise((res) => setTimeout(res, interval));
    }
    throw new Error("İşlem zaman aşımına uğradı");
}

export const kusurSorgu = {
    sabitler: () => api.get(`/v1/admin/operasyon/kusur-sorgu/sabitler/`).then((r) => r.data),
    dosyaListe: (params = {}) =>
        api.get(`/v1/admin/operasyon/kusur-sorgu/dosyalar/`, { params }).then((r) => r.data),
    dosyaOtomatikDoldur: (dosyaId, payload = {}) =>
        api.post(`/v1/admin/operasyon/kusur-sorgu/dosya/${dosyaId}/otomatik-doldur/`, { async: 1, deep_tutanak: true, ...payload }).then((r) => r.data),
    dosyaEvraklar: (dosyaId) =>
        api.get(`/v1/admin/operasyon/kusur-sorgu/dosya/${dosyaId}/evraklar/`).then((r) => r.data),
    hizliOku: (formData) => {
        formData.append("async", "1");
        return api.post(`/v1/admin/operasyon/kusur-sorgu/hizli-oku/`, formData, { timeout: 60000 }).then((r) => r.data);
    },
    analiz: (formData) => {
        formData.append("async", "1");
        return api.post(`/v1/admin/operasyon/kusur-sorgu/analiz/`, formData, { timeout: 60000 }).then((r) => r.data);
    },
    kaydet: (payload) => api.post(`/v1/admin/operasyon/kusur-sorgu/kaydet/`, payload).then((r) => r.data),
    liste: (params = {}) => api.get(`/v1/admin/operasyon/kusur-sorgu/liste/`, { params }).then((r) => r.data),
    detay: (id) => api.get(`/v1/admin/operasyon/kusur-sorgu/detay/${id}/`).then((r) => r.data),
    sil: (id) => api.delete(`/v1/admin/operasyon/kusur-sorgu/detay/${id}/`).then((r) => r.data),
    dosyaOnDoldur: (dosyaId) =>
        api.get(`/v1/admin/operasyon/kusur-sorgu/dosya/${dosyaId}/on-doldur/`).then((r) => r.data),
    pdf: async (payload) => {
        const r = await api.post(`/v1/admin/operasyon/kusur-sorgu/pdf/`, payload, { responseType: "blob" });
        return r.data;
    },
};

/** Giriş yapan admin kullanıcı profili */
export const fetchProfilim = () =>
    api.get(`/v1/admin/profilim/`).then((r) => r.data);

export const updateProfilim = (payload) =>
    api.patch(`/v1/admin/profilim/`, payload).then((r) => r.data);

export const changeProfilimSifre = (payload) =>
    api.post(`/v1/admin/profilim/sifre/`, payload).then((r) => r.data);

export const uploadProfilimAvatar = (file, onUploadProgress) => {
    const fd = new FormData();
    fd.append("avatar", file);
    return api
        .post(`/v1/admin/profilim/avatar/`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress,
        })
        .then((r) => r.data);
};

export const removeProfilimAvatar = () =>
    api.delete(`/v1/admin/profilim/avatar/`).then((r) => r.data);

/** Müvekkil portal modül ayarları (admin) */
export const muvekkilPanelAyar = {
    get: () => api.get(`/v1/admin/operasyon/muvekkil-panel-ayar/`).then((r) => r.data),
    patch: (payload) =>
        api.patch(`/v1/admin/operasyon/muvekkil-panel-ayar/`, payload).then((r) => r.data),
};
